// frontend/src/components/playbook/SignalRules.tsx
//
// Playbook › 1. The funnel › "Every rule, explained": each check a signal
// must pass before Telegram sends it, for a beginner — what it means in plain
// words, a small picture, a worked example with numbers, the line it shows on
// the desk, and the game that practises it. The numbers are the engine's own
// (backend signals/evaluateSignal.ts, setupClassifier.ts, setupChecklist.ts,
// enhancedConfluence.ts; frontend games/rules.ts mirrors them).
//
// Each rule has an anchor (#rule-…) so the desk checklist can link straight
// to it (playbook/ruleAnchors.ts).

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Chip } from "@heroui/react";
import { Gamepad2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Candle } from "./Candle";

type Tag = "Required" | "Graded" | "Sets the size" | "Gate" | "Not a gate";
const TAG_COLOR: Record<Tag, "danger" | "warning" | "accent" | "default" | "success"> = {
  Required: "danger",
  Graded: "warning",
  "Sets the size": "accent",
  Gate: "danger",
  "Not a gate": "default",
};

interface Rule {
  id: string;
  title: string;
  tag: Tag;
  /** What it means, in plain words. */
  plain: ReactNode;
  picture?: ReactNode;
  caption?: string;
  /** A worked example: one line each. */
  example: ReactNode[];
  /** The line you see on the desk for it. */
  desk?: string;
  /** Games that practise it: [title, level]. */
  games?: Array<[string, string]>;
}

const GROUPS: Array<{ title: string; sub: string; rules: Rule[] }> = [
  {
    title: "Direction: which way may we trade?",
    sub: "Before anything else, the engine decides whether this coin is allowed buys, sells, or nothing.",
    rules: [
      {
        id: "rule-4h-bias",
        title: "4h bias",
        tag: "Required",
        plain: (
          <>
            The <em>bias</em> is the direction of the bigger trend, read on the <B>4-hour chart</B> (each candle = 4 hours). Day traders don't guess
            direction; they borrow it from the bigger chart. The engine asks three questions, gives each a vote, and adds them up. If the total
            leans clearly up the bias is <B>bullish</B> (only buys allowed); clearly down it is <B>bearish</B> (only sells); in between it is{" "}
            <B>neutral</B>, and the trend-pullback setup waits.
          </>
        ),
        picture: <BiasDiagram />,
        caption: "A bullish 4h chart: the fast EMA above the medium above the slow one, and each swing high higher than the one before.",
        example: [
          <>EMAs: EMA 20 (64,800) above EMA 50 (63,900) above EMA 200 (61,200), stacked up → <Up>+0.4 up</Up></>,
          <>RSI 14 = 58, over 55 → <Up>+0.3 up</Up> (under 45 would vote down; 45–55 gives 0.1 to each side)</>,
          <>Last 3 swing highs: 62,900 → 63,700 → 65,100, each higher → <Up>+0.3 up</Up></>,
          <>Total: up 1.0, down 0 → net <B>+1.0</B>, more than +0.2 → <B>bullish: buys only</B></>,
        ],
        desk: "4h bias bullish · Now: 4h bias is bullish (confidence 100%)",
        games: [["Read the 4h bias", "Intermediate"], ["With the 4h bias?", "Intermediate"]],
      },
      {
        id: "rule-btc",
        title: "BTC's trend",
        tag: "Sets the size",
        plain: (
          <>
            Most altcoins follow Bitcoin. When BTC is trending <B>hard</B> one way, a signal on another coin that goes the <B>other</B> way is
            swimming against the current. The engine still sends it, but at <B>half your usual risk</B>, with the line “Against BTC · half risk”.
            Turn the switch off in Settings › Signal rules and these are blocked instead. When BTC is calm, this rule does nothing.
          </>
        ),
        picture: <BtcDiagram />,
        caption: "BTC is falling hard, and the engine finds a buy on SOL: it goes against BTC, so it trades at half risk.",
        example: [
          <>Your usual risk: $5 a trade (0.5% of $1,000)</>,
          <>BTC trending hard down; the engine sends a <B>SOL buy</B> → against BTC</>,
          <>Risk on this one: $5 × 0.5 = <B>$2.50</B>. Over 18 months these trades came out about even, so they get half the money.</>,
        ],
        desk: "Against BTC: half risk",
        games: [["Half-risk size", "Intermediate"]],
      },
      {
        id: "rule-1h-trend",
        title: "1h trend: EMA 20 vs EMA 50",
        tag: "Required",
        plain: (
          <>
            An <em>EMA</em> (exponential moving average) is the average price of the last candles, drawn as a line. The <B>EMA 20</B> (blue)
            follows the last 20 hours closely; the <B>EMA 50</B> (violet) is slower. When the fast line is <B>above</B> the slow one, recent
            prices are higher than older ones: an uptrend, so the engine only looks for buys. Below: a downtrend, sells only.
          </>
        ),
        picture: <TrendDiagram />,
        caption: "Left: EMA 20 above EMA 50, an uptrend (buys). Right: EMA 20 below EMA 50, a downtrend (sells).",
        example: [
          <>SOL 1h: EMA 20 = 146.20, EMA 50 = 143.90</>,
          <>146.20 is above 143.90 → <B>uptrend: buys only</B>. A sell signal here would fail this rule.</>,
        ],
        desk: "1h uptrend (EMA20 > EMA50) · Now: EMA20 146.20 vs EMA50 143.90",
        games: [["Which EMA is on top?", "Beginner"], ["Do the timeframes agree?", "Expert"]],
      },
    ],
  },
  {
    title: "Location: is price in the right place?",
    sub: "Even in an uptrend, buying after a big run is buying the top. The engine waits for price to come back.",
    rules: [
      {
        id: "rule-zone",
        title: "The pullback zone",
        tag: "Graded",
        plain: (
          <>
            The <em>zone</em> is the area around the two EMAs where buyers usually come back. Price must be within <B>1.5 ATR</B> of the EMA 20 or
            the EMA 50. <em>ATR</em> (average true range) is simply <B>the average size of one candle</B> over the last 14: if SOL candles move
            $1.20 on average, 1 ATR = $1.20 and 1.5 ATR = $1.80. Using ATR instead of a fixed % makes the rule fit calm and wild coins alike.
          </>
        ),
        picture: <ZoneDiagram />,
        caption: "Price runs far above the EMAs (too far: wait), then dips back into the shaded zone (in the zone).",
        example: [
          <>SOL: EMA 20 = 146.20, ATR = 1.20 → 1.5 ATR = 1.80</>,
          <>Price 151.00 is 4.8 away from the EMA 20 (4 ATR) → <Down>too far, wait</Down></>,
          <>Price drops to 147.50: 1.3 away (1.1 ATR) → <Up>in the zone</Up></>,
        ],
        desk: "Price reaches the EMA20/50 zone · Now: Price 147.50 is 1.1 ATR from the EMAs",
        games: [["In the pullback zone?", "Intermediate"], ["Click the pullback zone", "Intermediate"]],
      },
      {
        id: "rule-pullback",
        title: "A real pullback",
        tag: "Graded",
        plain: (
          <>
            A <em>pullback</em> is a short dip against the trend: the market takes a breath. The engine wants the dip to be at least{" "}
            <B>0.8 ATR</B> deep, measured from the recent high (last 8 candles) down to the lowest low of the last 4 candles. A dip of a few
            cents is just noise, not a pause worth buying. (Honest note: as written this check is loose and passes on most charts.)
          </>
        ),
        picture: <DepthDiagram />,
        caption: "From the recent high down to the pullback's low: at least 0.8 ATR.",
        example: [
          <>Recent high 150.40, pullback low 148.90 → depth 1.50</>,
          <>ATR 1.20 → 1.50 ÷ 1.20 = <B>1.25 ATR</B>, more than 0.8 → <Up>a real pullback</Up></>,
        ],
        desk: "A real pullback (≥ 0.8 ATR) · Now: Retraced 1.3 ATR from the recent high",
        games: [["A real pullback?", "Intermediate"]],
      },
      {
        id: "rule-higher-low",
        title: "Higher low intact",
        tag: "Required",
        plain: (
          <>
            An uptrend is a staircase: each dip (a <em>swing low</em>) stops <B>higher</B> than the one before. If this pullback falls{" "}
            <B>below</B> the previous swing low, the staircase is broken and the uptrend is in doubt, so no buy. (For sells: each bounce must stop
            lower than the last swing high.)
          </>
        ),
        picture: <HigherLowDiagram />,
        caption: "Left: the new low stays above the old one, the trend holds. Right: it breaks below, the trend is in doubt.",
        example: [
          <>Previous swing low 145.10; this pullback's low 148.90</>,
          <>148.90 is above 145.10 → <Up>higher low intact</Up>. At 144.50 it would be broken → no trade.</>,
        ],
        desk: "Higher low intact · Now: Pullback low 148.90 held above prior swing low 145.10",
        games: [["Higher or lower swing?", "Beginner"], ["Is the trend broken?", "Expert"]],
      },
    ],
  },
  {
    title: "Timing: has the trigger fired?",
    sub: "Trend and location say “maybe”. The trigger candle says “now”.",
    rules: [
      {
        id: "rule-trigger",
        title: "The trigger candle",
        tag: "Required",
        plain: (
          <>
            A 1-hour candle must <B>close</B> showing buyers are back. One of three shapes counts: a <B>bullish engulfing</B> (a green candle whose
            body covers the red one before it), a <B>hammer</B> (small body on top, a lower wick at least twice the body: sellers pushed down and
            lost), or simply a <B>close above the last candle's high</B>. Only the close counts: a candle that looks good at :40 can still turn
            before :00. For sells, the mirrors: bearish engulfing, shooting star, close below the last low.
          </>
        ),
        picture: <TriggerDiagram />,
        caption: "The three buy triggers. Each one is judged only when its 1-hour candle has closed.",
        example: [
          <>Last candle: open 148.20, close 147.40 (red); new candle: open 147.40, close 148.90 (green)</>,
          <>The green body (147.40 → 148.90) covers the red one (148.20 → 147.40) → <Up>bullish engulfing</Up></>,
          <>The engine checks at 14:00:30, half a minute after the 14:00 close. Entry is at that close.</>,
        ],
        desk: "A trigger candle closes · Now: Last 1h candle: bullish engulfing",
        games: [["Trigger or not?", "Intermediate"], ["Read the wick", "Beginner"], ["When does it close?", "Beginner"]],
      },
      {
        id: "rule-volume",
        title: "Volume on the trigger",
        tag: "Graded",
        plain: (
          <>
            <em>Volume</em> is how much was traded during the candle. A trigger with almost no volume is a move nobody joined. The trigger candle's
            volume must be at least <B>0.5×</B> the average of the 20 candles before it. <B>1.2× or more</B> is strong and helps the A+ grade.
          </>
        ),
        picture: <VolumeDiagram />,
        caption: "The dashed line is the 20-candle average. The trigger candle (right) traded 1.4× that: strong.",
        example: [
          <>Average volume of the last 20 hours: 52,000 SOL</>,
          <>Trigger candle: 73,000 SOL → 73,000 ÷ 52,000 = <B>1.4×</B> → <Up>strong (A+)</Up></>,
          <>At 22,000 SOL it would be 0.42× → <Down>too weak: the engine skips it</Down></>,
        ],
        desk: "Volume on the trigger ≥ 0.5× avg · Now: 1.40× 20-bar average (strong — A+ tier)",
        games: [["Enough volume?", "Intermediate"], ["Busy or quiet candle?", "Beginner"]],
      },
      {
        id: "rule-rsi",
        title: "RSI in the reset band",
        tag: "Graded",
        plain: (
          <>
            <em>RSI</em> (relative strength index) is a 0–100 gauge of how hard price has been pushing. Above 70 means it ran up fast (stretched);
            below 30 means it fell fast. For a pullback buy we want RSI to have <B>cooled off</B>: between <B>35 and 60</B> is grade A; 30–65 still
            passes as B+. Outside that, the trend is either still overheated or already broken. Sells use 40–65 (B+ 35–70).
          </>
        ),
        picture: <RsiDiagram />,
        caption: "RSI cools from 72 into the 35–60 band as price pulls back: the reset the engine wants.",
        example: [
          <>RSI 14 on SOL 1h = 48 → inside 35–60 → <Up>A band</Up></>,
          <>RSI 63 → only inside 30–65 → passes as B+. RSI 71 → <Down>outside both: no trade</Down></>,
        ],
        desk: "RSI resets to 30–65 · Now: RSI 48",
        games: [["RSI in the band?", "Intermediate"]],
      },
    ],
  },
  {
    title: "The plan: is the trade worth it?",
    sub: "A good-looking setup still has to pay. These are hard gates: fail one and nothing is sent.",
    rules: [
      {
        id: "rule-reward",
        title: "Reward at least 1.5× the risk",
        tag: "Gate",
        plain: (
          <>
            <em>Risk</em> is the distance from the entry to the stop: what you lose if you're wrong. <em>Reward</em> is the distance to target 1.
            Target 1 must pay at least <B>1.5 times</B> the risk. Then you can be wrong more often than right and still not lose: at 1.5× you
            break even winning 40% of trades.
          </>
        ),
        picture: <RewardDiagram />,
        caption: "Red: what you risk (entry to stop). Green: what target 1 pays. The green box must be at least 1.5× the red one.",
        example: [
          <>Buy 148.90, stop 146.90 → risk 2.00 a coin</>,
          <>Target 1 at 152.20 → reward 3.30 → 3.30 ÷ 2.00 = <B>1.65×</B> → <Up>passes</Up></>,
          <>Target 1 at 151.00 would pay 2.10 = 1.05× → <Down>rejected</Down></>,
        ],
        games: [["Reward vs risk", "Beginner"], ["Which plan pays enough?", "Intermediate"], ["Place target 1", "Intermediate"]],
      },
      {
        id: "rule-costs",
        title: "Fees at most 0.25R",
        tag: "Gate",
        plain: (
          <>
            Every trade pays fees, slippage (a fill a little worse than the screen price) and funding: about <B>0.16% of the position</B> for a
            round trip. That's fixed, but your risk isn't: with a very tight stop the fees eat a big part of it. The engine rejects a plan whose
            costs are more than a quarter of the risk (<B>0.25R</B>). In practice: stops tighter than about 0.63% are too tight.
          </>
        ),
        example: [
          <>Costs: 0.05% fee + 0.025% slippage, twice (in and out) + funding ≈ <B>0.16%</B></>,
          <>Stop 1.2% away → 0.16 ÷ 1.2 = <B>0.13R</B> → <Up>passes</Up></>,
          <>Stop 0.4% away → 0.16 ÷ 0.4 = <B>0.39R</B> → <Down>rejected: you'd pay 39% of your risk to the exchange</Down></>,
        ],
        games: [["Fees vs your risk", "Intermediate"], ["How far is the stop?", "Beginner"]],
      },
      {
        id: "rule-room",
        title: "Room before the next level",
        tag: "Gate",
        plain: (
          <>
            Price often stalls at <B>yesterday's high and low</B> and <B>this week's opening price</B> (the PD high, PD low and Week open lines on the
            chart). A buy that starts just under one of them is pushing into a wall. The engine wants at least <B>0.5R</B> of open space between the
            entry and the first such level.
          </>
        ),
        picture: <RoomDiagram />,
        caption: "Left: yesterday's high sits just above the entry (0.3R): blocked. Right: 1.2R of room: fine.",
        example: [
          <>Buy 148.90, stop 146.90 → 1R = 2.00</>,
          <>Yesterday's high at 149.50 → room 0.60 ÷ 2.00 = <B>0.3R</B> → <Down>blocked</Down></>,
          <>Yesterday's high at 151.30 → 2.40 ÷ 2.00 = <B>1.2R</B> → <Up>room to move</Up></>,
        ],
        games: [["Room to the target?", "Intermediate"]],
      },
    ],
  },
  {
    title: "Final checks",
    sub: "Three filters the engine runs on everything else, and one thing that is not a filter.",
    rules: [
      {
        id: "rule-score",
        title: "The confluence score",
        tag: "Gate",
        plain: (
          <>
            <em>Confluence</em> means several separate tools agreeing. The engine asks six of them (the market's regime, candle patterns, indicators,
            Fibonacci levels, Wyckoff phase and Elliott waves) which way they lean, weights their votes, and scores each side out of 100. The winning
            side must score at least <B>40</B>, beat the other side by at least <B>15</B>, and not too many tools may be undecided.
          </>
        ),
        picture: <ScoreBars />,
        caption: "Bullish 62 against bearish 31: above 40, and 31 points ahead.",
        example: [
          <>Bullish 62, bearish 31 → winner 62 ≥ 40, margin 31 ≥ 15 → <Up>passes</Up></>,
          <>Bullish 44, bearish 36 → margin only 8 → <Down>a coin flip: rejected</Down></>,
        ],
        games: [["Which setup passes?", "Expert"]],
      },
      {
        id: "rule-chop",
        title: "No chop, no cold coin",
        tag: "Gate",
        plain: (
          <>
            Two last brakes. <B>Chop:</B> when the market is ranging or consolidating, the engine drops its high-confidence trend signals, and keeps
            medium ones only with a score of 65 or more. <B>Cold coin:</B> a coin whose last signals lost too often (at least 8 of them, winning under
            40%) is paused until it recovers.
          </>
        ),
        example: [
          <>DOGE: 9 recent signals, 3 won (33%) → <Down>paused: cold coin</Down></>,
          <>SOL: 10 recent signals, 5 won (50%) → <Up>allowed</Up></>,
        ],
      },
      {
        id: "rule-day-limits",
        title: "Your daily limits",
        tag: "Not a gate",
        plain: (
          <>
            Your limits (<B>3 trades a day</B>, or stop once the day has lost <B>3R</B>) do <B>not</B> stop a signal: the engine doesn't know if you
            traded. Once your day is LOCKED, Telegram still sends it, with “🔒 Desk LOCKED: study today's signals, don't trade them”. The signal
            passed the rules; you've used up your trades.
          </>
        ),
        example: [
          <>Today: −1R, −1R, +0.5R → 3 trades taken → <Down>LOCKED</Down></>,
          <>A SOL buy arrives at 16:00 → read it, maybe log it as practice, but no real money until 00:00 UTC</>,
        ],
        games: [["One more trade?", "Beginner"], ["After two losses", "Expert"]],
      },
      {
        id: "rule-range",
        title: "Range fade: its own rules",
        tag: "Required",
        plain: (
          <>
            The second setup, the <B>range fade</B>, trades a sideways market instead of a trend, so it swaps the location and timing rules: a range
            at least <B>1.5 ATR</B> tall with at least <B>2 touches</B> of each side in 30 candles, price within <B>1 ATR</B> of the edge, and a
            rejection wick longer than <B>1.2×</B> the candle body. The plan gates above (reward, fees, room) apply the same. See the picture in
            section 2.
          </>
        ),
        example: [
          <>Range floor 140.00, ceiling 146.00 (6.00 tall), ATR 1.20 → 5 ATR tall, 3 touches each side → a valid range</>,
          <>Candle at the floor: body 0.40, lower wick 0.90 → 0.90 ÷ 0.40 = 2.25× → <Up>rejection: buy</Up></>,
        ],
        desk: "Rejection wick > 1.2× body",
        games: [["Range-floor wick", "Expert"]],
      },
    ],
  },
];

/** Every rule a signal passes, grouped like the funnel: direction, location, timing, the plan, final checks. */
export function SignalRules() {
  let n = 0;
  return (
    <div id="rules" className="mt-12 scroll-mt-20">
      <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground">Every rule, explained</h3>
      <p className="mt-3">
        Every BUY or SELL that Telegram sends has passed all of these, in this order. Each one below says what it means in plain words, shows it in
        a picture, works through an example, and points to the game that practises it. On the desk, every checklist line has a{" "}
        <span className="font-semibold text-foreground">?</span> that jumps straight here.
      </p>
      <div className="mt-8 space-y-14">
        {GROUPS.map(g => (
          <div key={g.title}>
            <h4 className="text-[0.8125rem] font-semibold uppercase tracking-wider text-muted">{g.title}</h4>
            <p className="mt-1 text-[0.9375rem] text-muted">{g.sub}</p>
            <div className="mt-6 space-y-10">
              {g.rules.map(r => <RuleCard key={r.id} rule={r} n={++n} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RuleCard({ rule: r, n }: { rule: Rule; n: number }) {
  return (
    <article id={r.id} aria-labelledby={`${r.id}-h`} className="scroll-mt-20">
      <div className="flex flex-wrap items-center gap-2.5">
        <span aria-hidden className="num flex size-6 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background">{n}</span>
        <h5 id={`${r.id}-h`} className="font-sans text-lg font-semibold tracking-[-0.01em] text-foreground">{r.title}</h5>
        <Chip size="sm" variant="soft" color={TAG_COLOR[r.tag]} className="font-semibold">{r.tag}</Chip>
      </div>
      <p className="mt-3">{r.plain}</p>
      {r.picture && (
        <figure className="mt-4">
          <div className="max-w-md">{r.picture}</div>
          {r.caption && <figcaption className="mt-2 max-w-md text-sm leading-relaxed text-muted">{r.caption}</figcaption>}
        </figure>
      )}
      <div className="mt-4 rounded-xl border border-border bg-surface px-4 py-3.5">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted">Example</p>
        <ul className="mt-2 space-y-1.5 text-[0.9375rem]">
          {r.example.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
      </div>
      {(r.desk || r.games) && (
        <div className="mt-3 flex flex-col gap-1.5 text-sm text-muted">
          {r.desk && <p><span className="font-semibold text-foreground/80">On the desk:</span> <span className="num text-[0.8125rem]">{r.desk}</span></p>}
          {r.games && (
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <Gamepad2 className="size-4 text-accent-soft-foreground" aria-hidden />
              <span className="font-semibold text-foreground/80">Practise it:</span>
              {r.games.map(([title, level], i) => (
                <span key={title}>
                  <Link to="/games" className="text-foreground underline underline-offset-2">{title}</Link>
                  <span> ({level}){i < r.games!.length - 1 ? "," : ""}</span>
                </span>
              ))}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

// ─── text bits ───────────────────────────────────────────────────────────────

function B({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>;
}
function Up({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-profit">{children}</span>;
}
function Down({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-loss">{children}</span>;
}

// ─── pictures (inline SVG, theme colours via CSS vars; y grows downward) ─────

const FRAME = "h-auto w-full rounded-xl border border-border bg-surface";
type Bar = [number, number, number, number, number]; // x, open, close, high, low

function Candles({ bars, w = 9 }: { bars: Bar[]; w?: number }) {
  return <>{bars.map(([x, o, c, h, l], i) => <Candle key={i} x={x} o={o} c={c} h={h} l={l} w={w} />)}</>;
}

/** Diagram text with a halo in the card's colour, so it stays readable over bars and lines. */
function Label({ x, y, children, fill = "var(--muted)", anchor = "start" }: { x: number; y: number; children: ReactNode; fill?: string; anchor?: "start" | "middle" | "end" }) {
  return (
    <text x={x} y={y} fontSize={8} fill={fill} textAnchor={anchor} stroke="var(--surface)" strokeWidth={2.5} paintOrder="stroke" strokeLinejoin="round">
      {children}
    </text>
  );
}

/** A rising 4h chart: EMAs stacked 20 > 50 > 200 and three higher swing highs. */
function BiasDiagram() {
  const bars: Bar[] = [
    [14, 140, 130, 127, 143], [28, 130, 120, 116, 133], [42, 120, 112, 108, 123], [56, 112, 120, 110, 124],
    [70, 120, 126, 117, 129], [84, 126, 114, 111, 128], [98, 114, 102, 99, 116], [112, 102, 94, 90, 105],
    [126, 94, 102, 92, 106], [140, 102, 106, 99, 109], [154, 106, 94, 91, 108], [168, 94, 82, 79, 96],
    [182, 82, 72, 68, 85], [196, 72, 78, 70, 81],
  ];
  const highs: Array<[number, number, string]> = [[42, 108, "high 1"], [112, 90, "high 2"], [182, 68, "high 3"]];
  return (
    <svg viewBox="0 0 260 170" className={FRAME} role="img" aria-label="A bullish 4-hour chart with stacked EMAs and rising swing highs">
      <path d="M0 152 C 80 132, 150 104, 210 80" stroke="var(--chart-ema-fast)" strokeWidth={1.5} fill="none" />
      <path d="M0 160 C 80 144, 150 120, 210 100" stroke="var(--chart-ema-slow)" strokeWidth={1.5} fill="none" />
      <path d="M0 166 C 90 158, 160 144, 210 132" stroke="var(--chart-ema-long)" strokeWidth={1.5} fill="none" />
      <Candles bars={bars} />
      {highs.map(([x, y, t]) => (
        <g key={t}>
          <circle cx={x} cy={y - 4} r={2.5} fill="var(--warning)" />
          <Label x={x} y={y - 9} anchor="middle" fill="var(--warning)">{t}</Label>
        </g>
      ))}
      <Label x={214} y={82} fill="var(--chart-ema-fast)">EMA 20</Label>
      <Label x={214} y={102} fill="var(--chart-ema-slow)">EMA 50</Label>
      <Label x={214} y={134} fill="var(--chart-ema-long)">EMA 200</Label>
      <Label x={214} y={24} fill="var(--foreground)">RSI 58</Label>
      <Label x={214} y={14}>4h candles</Label>
    </svg>
  );
}

/** BTC falling hard; a buy signal on an altcoin goes against it. */
function BtcDiagram() {
  return (
    <svg viewBox="0 0 260 130" className={FRAME} role="img" aria-label="BTC falling while an altcoin buy signal fires">
      <rect x={6} y={6} width={120} height={98} rx={6} fill="none" stroke="var(--border)" />
      <rect x={134} y={6} width={120} height={98} rx={6} fill="none" stroke="var(--border)" />
      <Label x={12} y={18} fill="var(--foreground)">BTC: trending hard down</Label>
      <polyline points="14,30 26,36 36,33 48,46 58,44 70,60 80,57 92,72 104,70 118,90" fill="none" stroke="var(--loss)" strokeWidth={2} />
      <Label x={140} y={18} fill="var(--foreground)">SOL: engine says BUY</Label>
      <polyline points="142,80 154,74 164,82 176,70 188,76 200,64 212,70 224,58" fill="none" stroke="var(--muted)" strokeWidth={1.5} />
      <path d="M228 70 l6 -12 l6 12 z" fill="var(--profit)" />
      <Label x={234} y={80} anchor="middle" fill="var(--profit)">BUY</Label>
      <Label x={130} y={120} anchor="middle" fill="var(--warning)">against BTC → half risk (or blocked if the switch is off)</Label>
    </svg>
  );
}

/** EMA 20 above EMA 50 (uptrend) vs below (downtrend). */
function TrendDiagram() {
  return (
    <svg viewBox="0 0 260 110" className={FRAME} role="img" aria-label="EMA 20 above EMA 50 is an uptrend; below is a downtrend">
      <rect x={6} y={6} width={120} height={84} rx={6} fill="none" stroke="var(--border)" />
      <rect x={134} y={6} width={120} height={84} rx={6} fill="none" stroke="var(--border)" />
      <path d="M14 76 C 50 64, 80 42, 118 22" stroke="var(--chart-ema-fast)" strokeWidth={2} fill="none" />
      <path d="M14 82 C 50 74, 80 58, 118 42" stroke="var(--chart-ema-slow)" strokeWidth={2} fill="none" />
      <Label x={16} y={22} fill="var(--chart-ema-fast)">EMA 20 on top</Label>
      <path d="M142 22 C 178 34, 208 56, 246 76" stroke="var(--chart-ema-fast)" strokeWidth={2} fill="none" />
      <path d="M142 16 C 178 24, 208 40, 246 58" stroke="var(--chart-ema-slow)" strokeWidth={2} fill="none" />
      <Label x={246} y={88} anchor="end" fill="var(--chart-ema-fast)">EMA 20 below</Label>
      <Label x={66} y={104} anchor="middle" fill="var(--profit)">uptrend → buys only</Label>
      <Label x={194} y={104} anchor="middle" fill="var(--loss)">downtrend → sells only</Label>
    </svg>
  );
}

/** Price far above the EMA zone, then back inside it. */
function ZoneDiagram() {
  const bars: Bar[] = [
    [14, 118, 110, 107, 121], [28, 110, 100, 97, 113], [42, 100, 86, 82, 103], [56, 86, 72, 68, 89],
    [70, 72, 60, 56, 75], [84, 60, 52, 48, 64], [98, 52, 62, 50, 66], [112, 62, 74, 60, 77],
    [126, 74, 86, 72, 90], [140, 86, 98, 84, 102], [154, 98, 108, 95, 112], [168, 110, 102, 99, 118],
  ];
  return (
    <svg viewBox="0 0 260 160" className={FRAME} role="img" aria-label="The pullback zone around the EMAs">
      <path d="M0 132 C 80 122, 160 100, 210 88 L 210 124 C 160 136, 80 152, 0 160 Z" fill="var(--profit)" opacity={0.12} />
      <path d="M0 140 C 80 130, 160 110, 210 98" stroke="var(--chart-ema-fast)" strokeWidth={1.5} fill="none" />
      <path d="M0 150 C 80 142, 160 124, 210 112" stroke="var(--chart-ema-slow)" strokeWidth={1.5} fill="none" />
      <Candles bars={bars} />
      <Label x={84} y={40} anchor="middle" fill="var(--loss)">too far: wait</Label>
      <Label x={160} y={152} anchor="middle" fill="var(--profit)">back in the zone</Label>
      <line x1={214} x2={214} y1={88} y2={124} stroke="var(--muted)" strokeWidth={1} />
      <line x1={211} x2={217} y1={88} y2={88} stroke="var(--muted)" strokeWidth={1} />
      <line x1={211} x2={217} y1={124} y2={124} stroke="var(--muted)" strokeWidth={1} />
      <Label x={220} y={100}>zone =</Label>
      <Label x={220} y={109}>EMAs</Label>
      <Label x={220} y={118}>±1.5 ATR</Label>
    </svg>
  );
}

/** Recent high, then a dip measured in ATR. */
function DepthDiagram() {
  const bars: Bar[] = [
    [20, 120, 104, 100, 123], [36, 104, 90, 86, 107], [52, 90, 76, 72, 93], [68, 76, 64, 58, 79],
    [84, 64, 76, 62, 79], [100, 76, 88, 73, 91], [116, 88, 98, 85, 101], [132, 98, 92, 88, 104],
  ];
  return (
    <svg viewBox="0 0 260 140" className={FRAME} role="img" aria-label="A pullback measured from the recent high">
      <Candles bars={bars} w={10} />
      <line x1={60} x2={200} y1={58} y2={58} stroke="var(--foreground)" strokeDasharray="3 2" strokeWidth={1} />
      <Label x={204} y={61} fill="var(--foreground)">recent high</Label>
      <line x1={110} x2={200} y1={104} y2={104} stroke="var(--foreground)" strokeDasharray="3 2" strokeWidth={1} />
      <Label x={204} y={107} fill="var(--foreground)">pullback low</Label>
      <line x1={180} x2={180} y1={60} y2={102} stroke="var(--warning)" strokeWidth={1.5} markerEnd="url(#depth-arrow)" />
      <defs>
        <marker id="depth-arrow" viewBox="0 0 6 6" refX={3} refY={3} markerWidth={6} markerHeight={6} orient="auto">
          <path d="M0 0 L6 3 L0 6 z" fill="var(--warning)" />
        </marker>
      </defs>
      <Label x={176} y={84} anchor="end" fill="var(--warning)">depth ≥ 0.8 ATR</Label>
    </svg>
  );
}

/** A staircase that holds vs one that breaks. */
function HigherLowDiagram() {
  return (
    <svg viewBox="0 0 260 120" className={FRAME} role="img" aria-label="A higher low that holds and one that breaks">
      <rect x={6} y={6} width={120} height={96} rx={6} fill="none" stroke="var(--border)" />
      <rect x={134} y={6} width={120} height={96} rx={6} fill="none" stroke="var(--border)" />
      <polyline points="14,88 36,50 54,74 80,30 98,58 118,22" fill="none" stroke="var(--foreground)" strokeWidth={1.5} />
      <circle cx={54} cy={74} r={3} fill="var(--profit)" />
      <circle cx={98} cy={58} r={3} fill="var(--profit)" />
      <Label x={54} y={86} anchor="middle" fill="var(--profit)">low 1</Label>
      <Label x={98} y={70} anchor="middle" fill="var(--profit)">low 2 higher</Label>
      <polyline points="142,88 164,50 182,74 208,30 230,92" fill="none" stroke="var(--foreground)" strokeWidth={1.5} />
      <line x1={182} x2={250} y1={74} y2={74} stroke="var(--loss)" strokeDasharray="3 2" strokeWidth={1} />
      <circle cx={182} cy={74} r={3} fill="var(--profit)" />
      <circle cx={230} cy={92} r={3} fill="var(--loss)" />
      <Label x={182} y={86} anchor="middle" fill="var(--muted)">low 1</Label>
      <Label x={226} y={100} anchor="end" fill="var(--loss)">broke below</Label>
      <Label x={66} y={115} anchor="middle" fill="var(--profit)">held: buys allowed</Label>
      <Label x={194} y={115} anchor="middle" fill="var(--loss)">broken: no buy</Label>
    </svg>
  );
}

/** The three buy triggers, two candles each: the one before, and the trigger. */
function TriggerDiagram() {
  return (
    <svg viewBox="0 0 260 130" className={FRAME} role="img" aria-label="Bullish engulfing, hammer, and close above the last high">
      {[0, 1, 2].map(i => <rect key={i} x={6 + i * 84} y={6} width={80} height={100} rx={6} fill="none" stroke="var(--border)" />)}
      {/* Bullish engulfing: small red, then a green body covering it. */}
      <Candle x={34} o={50} c={66} h={46} l={70} w={10} />
      <Candle x={54} o={70} c={42} h={38} l={74} w={14} />
      {/* Hammer: red, then a small body on top with a long lower wick. */}
      <Candle x={118} o={40} c={60} h={36} l={64} w={10} />
      <Candle x={138} o={62} c={56} h={54} l={92} w={10} />
      {/* Close above the last high. */}
      <Candle x={202} o={56} c={66} h={50} l={70} w={10} />
      <line x1={190} x2={238} y1={50} y2={50} stroke="var(--muted)" strokeDasharray="3 2" strokeWidth={1} />
      <Candle x={222} o={64} c={42} h={40} l={68} w={10} />
      <Label x={46} y={120} anchor="middle" fill="var(--profit)">bullish engulfing</Label>
      <Label x={130} y={120} anchor="middle" fill="var(--profit)">hammer</Label>
      <Label x={214} y={120} anchor="middle" fill="var(--profit)">close above last high</Label>
    </svg>
  );
}

/** Volume bars with the 20-candle average; the trigger bar is 1.4×. */
function VolumeDiagram() {
  const vols = [42, 55, 38, 61, 47, 52, 44, 58, 40, 50, 46, 70];
  const base = 100;
  const scale = 1.1;
  const avg = 50;
  return (
    <svg viewBox="0 0 260 120" className={FRAME} role="img" aria-label="Volume bars with the average line">
      {vols.map((v, i) => {
        const last = i === vols.length - 1;
        return (
          <rect key={i} x={14 + i * 18} y={base - v * scale} width={12} height={v * scale} rx={1.5}
            fill={last ? "var(--profit)" : "var(--muted)"} opacity={last ? 0.9 : 0.4} />
        );
      })}
      <line x1={10} x2={226} y1={base - avg * scale} y2={base - avg * scale} stroke="var(--foreground)" strokeDasharray="4 3" strokeWidth={1} />
      <Label x={10} y={base - avg * scale - 4} fill="var(--foreground)">20-candle average</Label>
      <line x1={10} x2={226} y1={base - avg * 0.5 * scale} y2={base - avg * 0.5 * scale} stroke="var(--loss)" strokeDasharray="2 3" strokeWidth={1} />
      <Label x={10} y={base - avg * 0.5 * scale + 10} fill="var(--loss)">0.5× (minimum)</Label>
      <Label x={226} y={base - 70 * scale - 4} anchor="middle" fill="var(--profit)">1.4×</Label>
      <Label x={226} y={114} anchor="middle" fill="var(--muted)">trigger</Label>
    </svg>
  );
}

/** RSI cooling from 72 into the 35–60 band. */
function RsiDiagram() {
  const y = (v: number) => 110 - ((v - 20) * 100) / 60; // RSI 20–80 → y 110–10
  const pts = [[14, 66], [34, 70], [54, 72], [74, 68], [94, 63], [114, 58], [134, 54], [154, 50], [174, 47], [194, 48]];
  return (
    <svg viewBox="0 0 260 120" className={FRAME} role="img" aria-label="RSI falling into the reset band">
      <rect x={10} y={y(65)} width={200} height={y(30) - y(65)} fill="var(--warning)" opacity={0.08} />
      <rect x={10} y={y(60)} width={200} height={y(35) - y(60)} fill="var(--warning)" opacity={0.16} />
      {[70, 60, 35, 30].map(v => (
        <g key={v}>
          <line x1={10} x2={210} y1={y(v)} y2={y(v)} stroke="var(--muted)" strokeDasharray="2 3" strokeWidth={0.75} />
          <Label x={214} y={y(v) + 3}>{v}</Label>
        </g>
      ))}
      <polyline points={pts.map(([x, v]) => `${x},${y(v!)}`).join(" ")} fill="none" stroke="var(--warning)" strokeWidth={2} />
      <circle cx={194} cy={y(48)} r={3} fill="var(--profit)" />
      <Label x={190} y={y(48) + 12} anchor="end" fill="var(--profit)">48: in the band</Label>
      <Label x={40} y={y(72) - 5} fill="var(--loss)">72: stretched</Label>
      <Label x={232} y={y(48) + 3} fill="var(--warning)">A</Label>
      <Label x={232} y={y(32) + 3} fill="var(--muted)">B+</Label>
    </svg>
  );
}

/** Entry, stop and target 1 as boxes: risk (red) and reward (green). */
function RewardDiagram() {
  return (
    <svg viewBox="0 0 260 130" className={FRAME} role="img" aria-label="Risk box and reward box">
      <rect x={60} y={22} width={130} height={52} fill="var(--profit)" opacity={0.18} />
      <rect x={60} y={74} width={130} height={32} fill="var(--loss)" opacity={0.18} />
      <line x1={60} x2={190} y1={22} y2={22} stroke="var(--profit)" strokeWidth={1.5} />
      <line x1={60} x2={190} y1={74} y2={74} stroke="var(--foreground)" strokeWidth={1.5} />
      <line x1={60} x2={190} y1={106} y2={106} stroke="var(--loss)" strokeWidth={1.5} />
      <Label x={56} y={25} anchor="end" fill="var(--profit)">TP1 152.20</Label>
      <Label x={56} y={77} anchor="end" fill="var(--foreground)">entry 148.90</Label>
      <Label x={56} y={109} anchor="end" fill="var(--loss)">stop 146.90</Label>
      <Label x={125} y={50} anchor="middle" fill="var(--profit)">reward 3.30 = 1.65×</Label>
      <Label x={125} y={93} anchor="middle" fill="var(--loss)">risk 2.00 = 1R</Label>
      <Label x={196} y={50} fill="var(--muted)">≥ 1.5× ✓</Label>
    </svg>
  );
}

/** A key level just above the entry (blocked) vs well above it (room). */
function RoomDiagram() {
  return (
    <svg viewBox="0 0 260 120" className={FRAME} role="img" aria-label="Room between the entry and the next key level">
      <rect x={6} y={6} width={120} height={92} rx={6} fill="none" stroke="var(--border)" />
      <rect x={134} y={6} width={120} height={92} rx={6} fill="none" stroke="var(--border)" />
      {/* Blocked: 1R = 24 px (entry 66 → stop 90), the level 0.3R above the entry. */}
      <line x1={14} x2={118} y1={59} y2={59} stroke="var(--warning)" strokeDasharray="4 2" strokeWidth={1.2} />
      <Label x={16} y={55} fill="var(--warning)">yesterday's high</Label>
      <line x1={14} x2={118} y1={66} y2={66} stroke="var(--foreground)" strokeWidth={1.2} />
      <Label x={16} y={76} fill="var(--foreground)">entry</Label>
      <line x1={14} x2={118} y1={90} y2={90} stroke="var(--loss)" strokeWidth={1.2} />
      <Label x={16} y={87} fill="var(--loss)">stop (1R below)</Label>
      <Label x={116} y={55} anchor="end" fill="var(--loss)">0.3R</Label>
      {/* Room: the level 1.2R above the entry. */}
      <line x1={142} x2={246} y1={37} y2={37} stroke="var(--warning)" strokeDasharray="4 2" strokeWidth={1.2} />
      <Label x={144} y={33} fill="var(--warning)">yesterday's high</Label>
      <line x1={142} x2={246} y1={66} y2={66} stroke="var(--foreground)" strokeWidth={1.2} />
      <Label x={144} y={76} fill="var(--foreground)">entry</Label>
      <line x1={142} x2={246} y1={90} y2={90} stroke="var(--loss)" strokeWidth={1.2} />
      <Label x={144} y={87} fill="var(--loss)">stop (1R below)</Label>
      <line x1={236} x2={236} y1={39} y2={64} stroke="var(--profit)" strokeWidth={1} />
      <Label x={232} y={55} anchor="end" fill="var(--profit)">1.2R</Label>
      <Label x={66} y={112} anchor="middle" fill="var(--loss)">under 0.5R: blocked</Label>
      <Label x={194} y={112} anchor="middle" fill="var(--profit)">room to move</Label>
    </svg>
  );
}

/** The two sides' scores against the 40 line. */
function ScoreBars() {
  const rows = [
    { side: "Bullish", score: 62, tone: "bg-profit" },
    { side: "Bearish", score: 31, tone: "bg-loss" },
  ];
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-4" role="img" aria-label="Bullish 62, bearish 31, minimum 40">
      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.side} className="grid grid-cols-[4.5rem_minmax(0,1fr)_2rem] items-center gap-3 text-sm">
            <span className="text-foreground">{r.side}</span>
            <div className="relative h-3 rounded-full bg-default">
              <div className={cn("h-3 rounded-full", r.tone)} style={{ width: `${r.score}%`, opacity: 0.75 }} />
              <div className="absolute inset-y-[-4px] w-px bg-foreground" style={{ left: "40%" }} />
            </div>
            <span className="num text-right text-foreground">{r.score}</span>
          </div>
        ))}
      </div>
      <p className="mt-2 pl-[5.25rem] text-xs text-muted">The line is the minimum, 40. The winner also needs a 15-point lead.</p>
    </div>
  );
}
